import { Box, Flex, Grid, Heading, Text } from "@radix-ui/themes";
import { HomeConsultForm } from "~/components/home/HomeConsultForm";
import {
	Locale,
	useLocalization,
} from "~/components/localization/LocalizationProvider";
import { OptimizedImage } from "~/components/OptimizedImage";
import { Link } from "~/components/RouterLink";
import styles from "../live-seo-page.module.css";

type BlogPostCard = {
	href: string;
	image: string;
	title: string;
	spanishTitle: string;
	date: string;
	spanishDate: string;
};

const blogPosts: BlogPostCard[] = [
	{
		href: "/why-patients-are-choosing-invisalign-in-miami-top-reasons/",
		image: "/adobestock-invisalign.jpg",
		title: "Why Patients Are Choosing Invisalign",
		spanishTitle: "Por qué los pacientes eligen Invisalign",
		date: "May 19, 2026",
		spanishDate: "19 de mayo de 2026",
	},
	{
		href: "/smile-best-medicine/",
		image: "/smile-best-medicine.png",
		title: "Smile… It’s the Best Medicine",
		spanishTitle: "Sonreír... es la mejor medicina",
		date: "May 25, 2017",
		spanishDate: "25 de mayo de 2017",
	},
	{
		href: "/why-i-became-an-orthodontist/",
		image: "/miami-ortho-13.jpg",
		title: "Why did you become an orthodontist?",
		spanishTitle: "¿Por qué te convertiste en ortodoncista?",
		date: "November 23, 2016",
		spanishDate: "23 de noviembre de 2016",
	},
	{
		href: "/happy-halloween/",
		image: "/miami-ortho-4.jpg",
		title: "Happy Halloween!",
		spanishTitle: "¡Feliz Halloween!",
		date: "October 31, 2016",
		spanishDate: "31 de octubre de 2016",
	},
	{
		href: "/the-demolition-is-real/",
		image: "/miami-ortho-office-4.jpg",
		title: "The Demolition is Real",
		spanishTitle: "La demolición es real",
		date: "May 12, 2016",
		spanishDate: "12 de mayo de 2016",
	},
	{
		href: "/welcome-miami-orthodontics/",
		image: "/same-day.jpg",
		title: "Our Mission",
		spanishTitle: "Nuestra misión",
		date: "January 22, 2016",
		spanishDate: "22 de enero de 2016",
	},
];

type BlogLocalization = {
	title: string;
	posts: Array<{
		href: string;
		image: string;
		title: string;
		date: string;
	}>;
};

const blogLocalization = {
	[Locale.EN]: {
		title: "Our Blog",
		posts: blogPosts.map((post) => ({
			href: post.href,
			image: post.image,
			title: post.title,
			date: post.date,
		})),
	},
	[Locale.ES]: {
		title: "Nuestro blog",
		posts: blogPosts.map((post) => ({
			href: post.href,
			image: post.image,
			title: post.spanishTitle,
			date: post.spanishDate,
		})),
	},
} satisfies Record<Locale, BlogLocalization>;

export function BlogPage() {
	const getLocalization = useLocalization(blogLocalization);
	const posts = getLocalization("posts");

	return (
		<Flex direction="column">
			<Box className={styles.blogBanner}>
				<Box className={styles.blogBannerInner}>
					<Heading as="h1" className={styles.blogTitle}>
						{getLocalization("title")}
					</Heading>
				</Box>
			</Box>
			<Box className={styles.contentBand}>
				<Box className={styles.blogInner}>
					<Grid
						columns={{ initial: "1", sm: "2", lg: "3" }}
						gap="5"
						className={styles.blogGrid}
					>
						{posts.map((post) => {
							const card = (
								<Flex
									direction="column"
									gap="3"
									className={styles.postCard}
									key={post.href}
								>
									<OptimizedImage
										src={post.image}
										alt=""
										className={styles.postImage}
										loading="lazy"
										sizes="(max-width: 767px) 65vw, 33vw"
									/>
									<Text size="2" className={styles.lastmod}>
										{post.date}
									</Text>
									<Heading as="h3" className={styles.postCardTitle}>
										{post.title}
									</Heading>
								</Flex>
							);

							return post.href.startsWith("http") ? (
								<a href={post.href} className={styles.postLink} key={post.href}>
									{card}
								</a>
							) : (
								<Link
									to={post.href}
									className={styles.postLink}
									key={post.href}
								>
									{card}
								</Link>
							);
						})}
					</Grid>
				</Box>
			</Box>
			<Box className={styles.formWrap}>
				<HomeConsultForm />
			</Box>
		</Flex>
	);
}
